import { BorderBox } from "@/components/atoms/BorderBox";
import { DynamicHeightScrollArea } from "@/components/atoms/DynamicHeightScrollArea";
import { HoverPopover } from "@/components/atoms/HoverPopover";
import { MarkSearchedPhrase } from "@/components/atoms/MarkSearchedPhrase";
import DecisionModal from "@/components/custom/InfoPopups/DecisionModal";
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import { NavigationBarBreadCrumbs } from '@/components/molecules/BackNav';
import { DroppableContainer } from "@/components/molecules/DroppableContainer";
import { SearchAndFilterByCrmSystem } from "@/components/molecules/SearchAndFilterByCrmSystem";
import { Label } from "@/components/ui/label";
import { useAllCrmProjectsList } from "@/features/crm_project/hooks/useQuery/useAllCrmProjectsList";
import { DraggableCrmSystemItem } from "@/features/crm_system/components/molecules/DraggableCrmSystemItem";
import { crmRegistry } from "@/features/crm_system/lib/_core/crmRegistry";
import { CrmSystemIds } from "@/features/crm_system/lib/_core/crmSystem/crmSystem.types.gen";
import { parseAsCrmSystemId } from "@/features/crm_system/utils/nuqsParser";
import { OrganizationTypeWordingResolver } from "@/features/organization/components/atoms/OrganizationTypeWordingResolver";
import { useGetOrganizationCrmSystems } from "@/features/organization/hooks/utils/useGetOrganizationCrmSystems/useGetOrganizationCrmSystems";
import { ProjectRow } from "@/features/project/components/organisms/ProjectRow";
import { Tables } from "@/lib/supabase/supabase_types_override";
import useEditProject from "@/lib/useQuery/project/useEditProject";
import { useProjectsList } from "@/lib/useQuery/project/useProjectsList";
import { useOrganizationStore } from '@/lib/zustand/useStore';
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { Loader, Trash2, X, FileStack } from "lucide-react";
import { parseAsArrayOf, parseAsString, useQueryState } from "nuqs";
import { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { TooltipButton } from "@/components/atoms/TooltipButton";
import { CreateCrmProjectModal } from "@/features/crm_project_with_crm_config_selection/components/organisms/CreateCrmProjectModal";
import { EditCrmProjectModal } from "@/features/crm_project_with_crm_config_selection/components/organisms/EditCrmProjectModal";
import { useDeleteCrmProject } from "@/features/crm_project/hooks/useMutation/useDeleteCrmProject";
import { useGetOrganisationCrmList } from "@/features/crm/hooks/useQuery/useGetOrganisationCrmList";
import CreateCrmConfigModal from "@/features/crm_config/components/organisms/CreateCrmConfigModal";
import { SurveyCrmListDialog } from "@/features/surveys/components/organisms/SurveyCrmListDialog";

export function ManageProjects() {
    const { orgId } = useParams()
    const { organization } = useOrganizationStore();
    /* 
    * Filters
    */
    const orgCrmSystems = useGetOrganizationCrmSystems();
    const [selectedCrmSystems, setSelectedCrmSystems] = useQueryState<CrmSystemIds[]>('crmSystems', parseAsArrayOf(parseAsCrmSystemId).withDefault(orgCrmSystems.map(system => system.id)));
    const [searchTerm, setSearchTerm] = useQueryState<string>('searchTerm', parseAsString.withDefault(''));
    const handleCheckboxChange = (id: CrmSystemIds, checked: boolean) => {
        setSelectedCrmSystems((prev) =>
            checked ? [...prev, id] : prev.filter((itemId) => itemId !== id)
        );
    };
    /* 
    * Fetches
    */
    const { data: crmsData, isLoading: isLoadingCrmsData } = useGetOrganisationCrmList(organization?.id);
    const allCrmProjectsList = useAllCrmProjectsList({ whereCrmSystems: selectedCrmSystems, orgId: Number(orgId) });
    const projectsList = useProjectsList({ orgId: Number(orgId) });

    /* 
    * Filtering
    */
    const filteredProjects = projectsList.data?.filter(project => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();

        // Filter by project name
        if (project?.name?.toLowerCase().includes(searchLower)) return true;

        // Filter by project description
        if (project?.challenge?.toLowerCase().includes(searchLower)) return true;

        // Filter by linked CRM project name
        if (project.crm_project?.name?.toLowerCase().includes(searchLower)) return true;

        return false;
    }) ?? [];

    const filteredCrmProjects = allCrmProjectsList.data?.filter(crmProject => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();

        // Filter by CRM project name
        if (crmProject.name.toLowerCase().includes(searchLower)) return true;

        // Filter by CRM project description
        if (crmProject.description?.toLowerCase().includes(searchLower)) return true;

        return false;
    }) ?? [];
    /* 
    * Update Project
    */
    const [updatedProjectId, setUpdatedProjectId] = useState<Tables<'project'>['id'] | null>(null)
    const { mutateAsync: updateProject, isPending: isUpdatingProject } = useEditProject()

    const removeCrmProjectConnection = (projectId: string) => {
        updateProject({
            id: projectId,
            crm_project_id: null,
            crm_id: null
        })
    }
    const handleDragEnd = (result: DropResult) => {
        const { source, destination, draggableId } = result;

        if (!destination) return;

        if (source.droppableId === 'crm-projects' && destination.droppableId.startsWith('project-')) {
            const projectId = destination.droppableId.replace('project-', '');
            const draggedCrmProject = filteredCrmProjects.find(p => p.id.toString() === draggableId);
            const targetProject = filteredProjects.find(p => p.id.toString() === projectId);

            if (draggedCrmProject && targetProject) {
                if (targetProject.crm_project_id === draggedCrmProject.id) { // No change
                    SuccessPopup("The dragged CRM project is already linked to the target project.");
                    return;
                };
                if (
                    targetProject.crm_project_id !== draggedCrmProject.id
                    && targetProject.crm_project_id !== null
                    && targetProject.crm?.crm_system !== draggedCrmProject.crm.crm_system
                ) {
                    DecisionModal({
                        title: "Replace existing CRM project?",
                        message: "This project is already linked to a CRM project from different CRM system. Do you want to replace it with the new one? It might cause unexpected behavior",
                    }).then((decision) => {
                        if (decision.isConfirmed) {
                            setUpdatedProjectId(targetProject.id)
                            updateProject({
                                crm_id: draggedCrmProject.crm_id,
                                crm_project_id: draggedCrmProject.id,
                                id: targetProject.id
                            }).finally(() => {
                                setUpdatedProjectId(null)
                            });
                        }
                    })
                } else {
                    setUpdatedProjectId(targetProject.id)
                    updateProject({
                        crm_id: draggedCrmProject.crm_id,
                        crm_project_id: draggedCrmProject.id,
                        id: targetProject.id
                    }).finally(() => {
                        setUpdatedProjectId(null)
                    });
                }
            }
        }
    };
    const { mutateAsync: deleteCrmProject } = useDeleteCrmProject();
    const deleteCrmProjectHandler = (crmProjectId: Tables<'crm_project'>['id']) => {
        DecisionModal({
            title: "Are you sure?",
            message: "This action cannot be undone.",
        }).then((decision) => {
            if (decision.isConfirmed) {
                deleteCrmProject(crmProjectId);
            }
        });
    };
    const crmConfigurationsExist = !(crmsData?.length === 0 || crmsData === undefined || crmsData === null);

    const recordsByCrm = useMemo(() => {
        if(!allCrmProjectsList.data || !orgCrmSystems) return;

        const crmCounts: Record<string, number> = {};
        for (const crm of orgCrmSystems) {
            crmCounts[crm.id] = 0;
        }

        for (const record of allCrmProjectsList.data) {
            if (record?.crm?.crm_system && (record.crm.crm_system in crmCounts)) {
                crmCounts[record.crm.crm_system]++;
            }
        }

        return crmCounts;

    }, [allCrmProjectsList.data]);


    // SurveyCrmListDialog - logic
    const [isAddSurveyCrmDialogOpen, setAddSurveyCrmDialogOpen] = useState(false);
    const [selectedCrmProject, setSelectedCrmProject] = useState<null | Tables<'crm_project'>>(null);
    const openAddSurveyCrmDialog = (crmProject) => {
        // console.log("[ManageProjects] crmProject: ", crmProject);
        setSelectedCrmProject({
            ...crmProject,
            crm_project_id: crmProject.id,
            crm_project: {
                ...crmProject
            }
        });
        setAddSurveyCrmDialogOpen(true);
    }

    return (
        <>
            <SurveyCrmListDialog
                open={isAddSurveyCrmDialogOpen}
                onOpenChange={setAddSurveyCrmDialogOpen}
                project={selectedCrmProject}
            />

            <DragDropContext onDragEnd={handleDragEnd}>
                <div className="flex flex-col flex-1 gap-2">
                    <NavigationBarBreadCrumbs.Set
                        currentPageName={"Manage Projects"}
                        pages={[
                            {
                                title: "Home",
                                goTo: `/welcome`
                            },
                            {
                                title: organization?.name || "-",
                                goTo: `/organization/${orgId}`
                            },

                        ]}
                    />

                    <SearchAndFilterByCrmSystem
                        selectedCrmSystems={selectedCrmSystems}
                        handleCheckboxChange={handleCheckboxChange}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        placeholder="Search CRM projects..."
                        recordsByCrm={recordsByCrm}
                    />
                    <div className="grid flex-1 grid-cols-2 gap-2">
                        <div className="flex flex-col flex-1">
                            <Label>Attach CRM Project Configuration to <OrganizationTypeWordingResolver capitalize plural /></Label>
                            {!isLoadingCrmsData && !crmConfigurationsExist && organization?.id && (
                                <BorderBox variant='danger' size='sm' className="mt-2">
                                    <div className="">
                                        No CRM Configuration found. You can browse your CRM Project configuration but you won't be able to create new ones until you <Link className="text-red-600 underline" to={`/organization/${orgId}/manage-organization`}>add at least one CRM Configuration to your organization</Link>.
                                    </div>
                                </BorderBox>
                            )}
                            <BorderBox className="flex-1 mt-2 bg-white">
                                {(() => {
                                    if (allCrmProjectsList.isLoading || isLoadingCrmsData) {
                                        return (
                                            <div className="flex items-center justify-center w-full">
                                                <Loader className="animate-spin" />
                                            </div>
                                        )
                                    }
                                    if (allCrmProjectsList.isError) {
                                        return (
                                            <div className="flex items-center justify-center w-full">
                                                Error loading CRM projects!
                                            </div>
                                        )
                                    }
                                    if (allCrmProjectsList.data === undefined || allCrmProjectsList.data === null || allCrmProjectsList.data.length === 0) {
                                        return (
                                            <div className="flex flex-col items-center justify-center w-full gap-2">
                                                <p>No CRM Project Configurations found.</p>
                                                <CreateCrmProjectModal />
                                            </div>
                                        )
                                    }
                                    return (
                                        <DynamicHeightScrollArea offset={60}>
                                            <Droppable droppableId="crm-projects">
                                                {(provided) => (
                                                    <div
                                                        className="flex flex-col gap-2"
                                                        ref={provided.innerRef}
                                                        {...provided.droppableProps}
                                                    >
                                                        {filteredCrmProjects.map((crmProject, index) => (
                                                            <Draggable
                                                                key={crmProject.id}
                                                                draggableId={crmProject.id.toString()}
                                                                index={index}
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <DraggableCrmSystemItem
                                                                        provided={provided}
                                                                        isDragging={snapshot.isDragging}
                                                                        crmSystem={crmProject.crm.crm_system}
                                                                    >
                                                                        <div className="flex flex-col gap-0.5 flex-grow">
                                                                            <MarkSearchedPhrase
                                                                                text={crmProject.name}
                                                                                searchTerm={searchTerm}
                                                                                // className="flex-1 min-w-0 font-medium line-clamp-1"
                                                                                className="font-medium"
                                                                            />

                                                                            <HoverPopover
                                                                                onHover
                                                                                content={
                                                                                    <MarkSearchedPhrase
                                                                                        text={crmProject.description || ''}
                                                                                        searchTerm={searchTerm}
                                                                                    />
                                                                                }
                                                                            >
                                                                                <MarkSearchedPhrase
                                                                                    text={crmProject.description || ''}
                                                                                    searchTerm={searchTerm}
                                                                                    className="flex-1 min-w-0 text-start line-clamp-1 text-muted-foreground"
                                                                                />
                                                                            </HoverPopover>
                                                                        </div>

                                                                        <div className="flex flex-wrap justify-end gap-1 ml-auto lg:flex-nowrap">
                                                                            <TooltipButton
                                                                                onClick={() => openAddSurveyCrmDialog(crmProject)}
                                                                                size='action'
                                                                                tooltipContent="Surveys Configuration"
                                                                            >
                                                                                <FileStack size={16} />
                                                                            </TooltipButton>


                                                                            <EditCrmProjectModal crmProject={crmProject} />

                                                                            <TooltipButton
                                                                                onClick={() => deleteCrmProjectHandler(crmProject.id)}
                                                                                size='action'
                                                                                variant='destructive'
                                                                                tooltipContent="Delete"
                                                                            >
                                                                                <Trash2 size={16} />
                                                                            </TooltipButton>
                                                                        </div>
                                                                    </DraggableCrmSystemItem>
                                                                )}
                                                            </Draggable>
                                                        ))}
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </Droppable>
                                        </DynamicHeightScrollArea>
                                    )
                                })()}
                            </BorderBox>
                        </div>
                        <div className="flex flex-col flex-1">
                            <Label><OrganizationTypeWordingResolver capitalize plural /></Label>
                            <BorderBox className="flex-1 mt-2 bg-white">
                                {(() => {
                                    if (projectsList.isLoading) {
                                        return (
                                            <div className="flex items-center justify-center w-full">
                                                <Loader className="animate-spin" />
                                            </div>
                                        )
                                    }
                                    if (projectsList.isError) {
                                        return (
                                            <div className="flex items-center justify-center w-full">
                                                Error loading <OrganizationTypeWordingResolver plural />!
                                            </div>
                                        )
                                    }
                                    return (
                                        <DynamicHeightScrollArea offset={60}>
                                            <div className="flex flex-col gap-2">
                                                {filteredProjects.map((project) => (
                                                    <ProjectRow
                                                        key={project.id}
                                                        project={project}
                                                        crmSystem={project.crm?.crm_system}
                                                        searchTerm={searchTerm}
                                                        isLoading={((updatedProjectId === project.id) || (isUpdatingProject))}
                                                    >
                                                        <DroppableContainer
                                                            droppableId={`project-${project.id}`}
                                                            placeholder="Drag and Drop selected configuration here to link it to this project"
                                                        >
                                                            {project.crm_project ? (
                                                                <BorderBox
                                                                    className="flex items-center w-auto gap-2 border-l-4"
                                                                    style={{ borderLeftColor: crmRegistry.getSystemById(project.crm?.crm_system)?.branding.color }}
                                                                    size='xs'
                                                                >
                                                                    <MarkSearchedPhrase
                                                                        text={project.crm_project.name}
                                                                        searchTerm={searchTerm}
                                                                    />

                                                                    <TooltipButton
                                                                        size='action-xs'
                                                                        variant='destructive'
                                                                        tooltipContent="Delete"
                                                                        onClick={() => removeCrmProjectConnection(project.id)}
                                                                    >
                                                                        <X size={12} />
                                                                    </TooltipButton>

                                                                </BorderBox>
                                                            ) : null}
                                                        </DroppableContainer>
                                                    </ProjectRow>
                                                ))}
                                            </div>
                                        </DynamicHeightScrollArea>
                                    )
                                })()}
                            </BorderBox>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <CreateCrmProjectModal />
                    </div>
                </div>
            </DragDropContext>
        </>
    )
}
